/**
 * EU VAT identification numbers (USt-IdNr.): offline format check per country prefix, the same
 * shapes the EU's VIES service accepts. Numbers from outside the EU (CHE-…, GB…, …) are free
 * text; only EU-prefixed numbers are validated, and an EU customer needs an EU-prefixed number.
 */
const EU_VAT_PATTERNS: Record<string, RegExp> = {
  AT: /^U\d{8}$/,
  BE: /^[01]\d{9}$/,
  BG: /^\d{9,10}$/,
  CY: /^\d{8}[A-Z]$/,
  CZ: /^\d{8,10}$/,
  DE: /^\d{9}$/,
  DK: /^\d{8}$/,
  EE: /^\d{9}$/,
  EL: /^\d{9}$/,
  ES: /^[A-Z0-9]\d{7}[A-Z0-9]$/,
  FI: /^\d{8}$/,
  FR: /^[A-HJ-NP-Z0-9]{2}\d{9}$/,
  HR: /^\d{11}$/,
  HU: /^\d{8}$/,
  IE: /^(\d{7}[A-W][A-I]?|\d[A-Z+*]\d{5}[A-W])$/,
  IT: /^\d{11}$/,
  LT: /^(\d{9}|\d{12})$/,
  LU: /^\d{8}$/,
  LV: /^\d{11}$/,
  MT: /^\d{8}$/,
  NL: /^\d{9}B\d{2}$/,
  PL: /^\d{10}$/,
  PT: /^\d{9}$/,
  RO: /^[1-9]\d{1,9}$/,
  SE: /^\d{10}01$/,
  SI: /^\d{8}$/,
  SK: /^\d{10}$/,
  XI: /^(\d{9}|\d{12}|GD\d{3}|HA\d{3})$/,
};

/** ISO country code -> VAT prefix (Greece uses EL, Northern Ireland XI under GB). */
const vatPrefixOf = (country: string) => (country === "GR" ? "EL" : country);

export const isEuCountry = (country: string) =>
  country !== "XI" && vatPrefixOf(country) in EU_VAT_PATTERNS;

/** "de 123 456-789" -> "DE123456789" for EU numbers; other numbers are only trimmed. */
export function normalizeVatId(value: string): string {
  const compact = value.toUpperCase().replace(/[\s.\-/]/g, "");
  return compact.slice(0, 2) in EU_VAT_PATTERNS ? compact : value.trim();
}

export type VatIssue = "format" | "euPrefix";

/** Why `value` (normalized) is not a valid VAT ID for a customer in `country`, or null. */
export function vatIdIssue(value: string, country: string): VatIssue | null {
  if (!value) return null;
  const prefix = value.slice(0, 2);
  const pattern = EU_VAT_PATTERNS[prefix];
  if (pattern) return pattern.test(value.slice(2)) ? null : "format";
  return isEuCountry(country) ? "euPrefix" : null;
}

/** Placeholder showing the expected shape for the customer's country. */
export function vatIdExample(country: string): string {
  const examples: Record<string, string> = {
    AT: "ATU12345678",
    DE: "DE123456789",
    FR: "FRXX123456789",
    NL: "NL123456789B01",
    IT: "IT12345678901",
    ES: "ESX1234567X",
    CH: "CHE-123.456.789 MWST",
    GB: "GB123456789",
    AE: "100123456700003",
    AU: "51 824 753 556",
    NZ: "123-456-789",
  };
  return examples[country] ?? (isEuCountry(country) ? `${vatPrefixOf(country)}…` : "");
}
